import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Check, AlertCircle, ExternalLink } from 'lucide-react';
import { AppShell, useShell } from '../components/AppShell';
import './StripeSetup.css';

const StripeSetupInner = () => {
  // Pull the signed-in user's own shop from the shared shell context.
  // Previously this page did `shops.select('*').limit(1).single()`, which
  // was a cross-tenant leak — Postgres would return whatever row it liked
  // first, and clicking "Connect Stripe" then minted a Stripe Standard
  // account on a foreign shop. Iteration_49 flagged this as CRITICAL.
  const { profile } = useShell();
  const shopId = profile?.shop_id;
  const [shop, setShop] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!shopId) return;
    fetchShopAndStatus();
  }, [shopId]);

  const fetchShopAndStatus = async () => {
    try {
      // Load THIS shop only, keyed off the caller's own shop_id.
      const { data: shopData, error: shopError } = await supabase
        .from('shops')
        .select('*')
        .eq('id', shopId)
        .single();

      if (shopError) throw shopError;
      setShop(shopData);

      // Check Stripe status — every backend Stripe endpoint now requires
      // an Authorization bearer, so pull the session before fetching.
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${BACKEND_URL}/api/stripe/connect/status/${shopData.id}`,
        { headers: { 'Authorization': `Bearer ${session?.access_token}` } }
      );
      const data = await response.json();
      setStripeStatus(data);
    } catch (error) {
      console.error('Error fetching shop/status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectStripe = async () => {
    setOnboarding(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${BACKEND_URL}/api/stripe/connect/onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          shop_id: shop.id,
          refresh_url: `${window.location.origin}/stripe-setup`,
          return_url: `${window.location.origin}/stripe-setup?success=true`
        })
      });

      const data = await response.json();

      if (data.success) {
        // Redirect to Stripe onboarding
        window.location.href = data.url;
      } else {
        alert(data.detail || 'Error creating onboarding link');
        setOnboarding(false);
      }
    } catch (error) {
      console.error('Error starting onboarding:', error);
      alert('Error connecting to Stripe');
      setOnboarding(false);
    }
  };

  if (loading) {
    return <div className="stripe-setup-loading">Loading...</div>;
  }

  if (!shop) {
    return <div className="stripe-setup-loading">Shop not found</div>;
  }

  return (
    <div className="main" data-testid="stripe-setup-page">
      <div className="topbar">
        <div>
          <h2>Stripe Payments</h2>
          <div className="sub">Accept deposits and get paid directly</div>
        </div>
      </div>

      <div className="body">
          <div className="panel">
            <div className="setup-content">
              {stripeStatus?.connected && stripeStatus?.charges_enabled ? (
                <div className="status-card success">
                  <div className="status-icon success">
                    <Check size={32} />
                  </div>
                  <h3>Stripe Connected</h3>
                  <p>Your account is set up and ready to accept payments. Deposits will be paid directly to your bank account.</p>
                  <div className="info-row">
                    <span className="label">Status:</span>
                    <span className="value connected">Active</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Account ID:</span>
                    <span className="value mono">{stripeStatus.account_id}</span>
                  </div>
                </div>
              ) : stripeStatus?.connected && !stripeStatus?.charges_enabled ? (
                <div className="status-card warning">
                  <div className="status-icon warning">
                    <AlertCircle size={32} />
                  </div>
                  <h3>Complete Your Setup</h3>
                  <p>Your Stripe account needs additional information to accept payments.</p>
                  <button 
                    className="btn p" 
                    onClick={handleConnectStripe}
                    disabled={onboarding}
                    data-testid="stripe-complete-setup-btn"
                  >
                    <ExternalLink size={16} />
                    {onboarding ? 'Opening Stripe...' : 'Complete Setup'}
                  </button>
                </div>
              ) : (
                <div className="status-card">
                  <div className="status-icon">
                    <AlertCircle size={32} />
                  </div>
                  <h3>Connect Stripe</h3>
                  <p>Connect your bank account to receive deposit payments directly from customers.</p>
                  
                  <div className="benefits">
                    <h4>What you&apos;ll need:</h4>
                    <ul>
                      <li>Business details and registration</li>
                      <li>Bank account information</li>
                      <li>Personal ID for verification</li>
                    </ul>
                    
                    <h4>How it works:</h4>
                    <ul>
                      <li>Customers pay deposits when booking</li>
                      <li>Money goes directly to your bank account</li>
                      <li>Instacut never touches your funds</li>
                      <li>Secure, PCI-compliant payments</li>
                    </ul>
                  </div>

                  <button 
                    className="btn p large" 
                    onClick={handleConnectStripe}
                    disabled={onboarding}
                    data-testid="stripe-connect-btn"
                  >
                    <ExternalLink size={16} />
                    {onboarding ? 'Opening Stripe...' : 'Connect Stripe Account'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
  );
};

const StripeSetup = () => (
  <AppShell>
    <StripeSetupInner />
  </AppShell>
);

export default StripeSetup;
